import React, { Component } from "react";
import * as d3 from "d3";
import { satelliteName } from "../../constellations";
import { latestPass, noLiveReason } from "../../ippStatus";

interface State {
  tooltipContent: string;
  tooltipTop: string;
  tooltipLeft: string;
  tooltipVisible: boolean;
}

interface Props {
  ionoData: any;
  stationInfo: any;
  stationId: string;
  constellation: string;
  handleOpenMapPanel: (d: any) => void;
  /** Click on an IPP track: open the TEC time series of that satellite */
  onSatelliteClick?: (sat: number) => void;
  /** Satellite whose time series is open: its track is highlighted */
  selectedSatellite?: number;
  /** Incremented by the parent to make the selected track blink (e.g. click on the time series) */
  flashKey?: number;
  /** Live status per satellite (data in the last seconds). Missing = unknown, treated as live */
  liveStatus?: Record<number, boolean>;
}

const COLORS = {
  ocean: "#f0f7ff",
  land: "#e2e8f0",
  landStroke: "#cbd5e1",
  graticule: "#e2e8f0",
  stationFill: "#0ea5e9",
  stationStroke: "#fff",
  stationPulse: "#0ea5e9",
  labelFill: "#334155",
};

class MapD3 extends Component<Props, State> {
  private svgRef!: SVGSVGElement;
  private projection: any;
  private path: any;
  private mapGroup: any;
  private contentGroup: any;   // satellite tracks
  private stationGroup: any;   // station marker (always on top)
  private zoomBehavior: any;
  private mapLoaded = false;
  private zoomK = 1;      // current zoom scale
  private drawnK = 1;     // zoom scale the tracks were last sampled at

  constructor(props: any) {
    super(props);
    this.state = {
      tooltipContent: "",
      tooltipTop: "0px",
      tooltipLeft: "0px",
      tooltipVisible: false,
    };
  }

  private showTooltip(evt: MouseEvent, content: string) {
    this.setState({
      tooltipVisible: true,
      tooltipContent: content,
      tooltipLeft: evt.offsetX + 15 + "px",
      tooltipTop: evt.offsetY - 10 + "px",
    });
  }

  private hideTooltip() {
    this.setState({ tooltipVisible: false });
  }

  private static readonly VB_WIDTH = 960;
  private static readonly VB_HEIGHT = 500;

  private initMap() {
    const svg = d3.select(this.svgRef);
    svg.selectAll("*").remove();
    this.zoomK = 1;
    this.drawnK = 1;

    const W = MapD3.VB_WIDTH;
    const H = MapD3.VB_HEIGHT;

    this.projection = d3
      .geoNaturalEarth1()
      .fitSize([W - 40, H - 20], { type: "Sphere" } as any)
      .translate([W / 2, H / 2]);
    this.path = d3.geoPath().projection(this.projection);

    const rootG = svg.append("g").attr("class", "map-root");

    // Ocean
    rootG
      .append("path")
      .datum({ type: "Sphere" } as any)
      .attr("d", this.path as any)
      .attr("fill", COLORS.ocean)
      .attr("stroke", COLORS.landStroke)
      .attr("stroke-width", 0.5)
      .attr("vector-effect", "non-scaling-stroke");

    // Graticule
    rootG
      .append("path")
      .datum(d3.geoGraticule10())
      .attr("d", this.path as any)
      .attr("fill", "none")
      .attr("stroke", COLORS.graticule)
      .attr("stroke-width", 0.3)
      .attr("vector-effect", "non-scaling-stroke");

    this.mapGroup = rootG.append("g").attr("class", "map-layer");
    // Tracks below, station on top — so marker is always clickable
    this.contentGroup = rootG.append("g").attr("class", "tracks-layer");
    this.stationGroup = rootG.append("g").attr("class", "station-layer");

    this.zoomBehavior = d3
      .zoom()
      .scaleExtent([1, 60])
      .translateExtent([[0, 0], [W, H]])
      .on("zoom", (evt: any) => {
        rootG.attr("transform", evt.transform);
        this.zoomK = evt.transform.k;
        this.applyZoomInvariant();
      })
      .on("end", () => {
        // Re-sample the tracks at the new zoom so they stay smooth when zoomed in
        if (Math.abs(this.zoomK - this.drawnK) / this.drawnK > 0.25) this.drawContent();
      });
    svg.call(this.zoomBehavior);

    d3.json("/world.geojson").then((worldData: any) => {
      this.mapGroup
        .selectAll("path")
        .data(worldData.features)
        .enter()
        .append("path")
        .attr("d", (d: any) => this.path(d))
        .attr("fill", COLORS.land)
        .attr("stroke", COLORS.landStroke)
        .attr("stroke-width", 0.4)
        .attr("vector-effect", "non-scaling-stroke");

      this.mapLoaded = true;
      this.drawContent();
    });
  }

  private drawContent() {
    if (!this.mapLoaded || !this.contentGroup) return;

    this.drawnK = this.zoomK;
    this.contentGroup.selectAll("*").remove();
    this.stationGroup.selectAll("*").remove();

    const { ionoData, stationInfo, stationId, constellation, selectedSatellite } = this.props;

    // --- Satellite tracks (drawn FIRST, below station) ---
    if (ionoData) {
      const satellites = Object.keys(ionoData).filter(
        (k) => ionoData[k] && ionoData[k].length > 1
      );

      if (satellites.length > 0) {
        const colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(satellites);
        const lineGen = d3.line<[number, number]>().x(d => d[0]).y(d => d[1]);
        const minDist2 = Math.pow(0.5 / this.zoomK, 2);

        for (const sat of satellites) {
          // Latest continuous pass only (no line joining it to a previous pass hours earlier)
          const points: any[] = latestPass(ionoData[sat]);
          if (!points || points.length < 2) continue;

          const color = colorScale(sat);
          const satLabel = satelliteName(constellation, parseInt(sat));

          // Project points to screen space, downsample to avoid overdraw.
          // Data is newest-first — reverse so path goes tail→head.
          const reversed = [...points].reverse();
          const projected: [number, number][] = [];
          let prevX = -Infinity, prevY = -Infinity;
          for (const p of reversed) {
            const pt = this.projection([p.lon, p.lat]);
            if (!pt) continue;
            // Skip points closer than ~0.5 screen px to previous (reduces DOM load);
            // threshold shrinks with zoom so tracks stay smooth when zoomed in
            const dx = pt[0] - prevX, dy = pt[1] - prevY;
            if (dx * dx + dy * dy < minDist2) continue;
            projected.push(pt as [number, number]);
            prevX = pt[0]; prevY = pt[1];
          }
          if (projected.length < 2) continue;

          const satNum = parseInt(sat);
          const isSelected = selectedSatellite === satNum;
          const hasSelection = !!selectedSatellite && selectedSatellite > 0;
          const isLive = this.props.liveStatus?.[satNum] !== false;
          const tooltip = isLive
            ? satLabel + " — click to view TEC series"
            : satLabel + " — no live data: " + noLiveReason(ionoData[sat]);
          const onClick = () => {
            this.hideTooltip();
            this.props.onSatelliteClick?.(satNum);
          };
          const trackGroup = this.contentGroup
            .append("g")
            .attr("class", isSelected ? "sat-track selected" : "sat-track")
            .style("cursor", "pointer")
            .on("mousemove", (event: MouseEvent) => this.showTooltip(event, tooltip))
            .on("mouseout", () => this.hideTooltip())
            .on("click", onClick);

          const d = lineGen(projected);
          const dimmed = hasSelection && !isSelected;

          // White casing under the line: contrast over land, ocean and other tracks.
          // All strokes are non-scaling: constant on-screen width at any zoom.
          trackGroup
            .append("path")
            .attr("d", d)
            .attr("fill", "none")
            .attr("stroke", "#fff")
            .attr("stroke-width", isSelected ? 6 : 4)
            .attr("stroke-linecap", "round")
            .attr("stroke-linejoin", "round")
            .attr("vector-effect", "non-scaling-stroke")
            .attr("opacity", dimmed ? 0.3 : isLive ? 0.9 : 0.5);

          trackGroup
            .append("path")
            .attr("class", "track-line")
            .attr("d", d)
            .attr("fill", "none")
            .attr("stroke", color)
            .attr("stroke-width", isSelected ? 3.5 : 2)
            .attr("stroke-linecap", isLive ? "round" : "butt")
            .attr("stroke-linejoin", "round")
            .attr("vector-effect", "non-scaling-stroke")
            // Not live: dashed and lighter
            .attr("stroke-dasharray", isLive ? null : "6 4")
            .attr("opacity", dimmed ? 0.3 : isLive ? 0.95 : 0.55);

          // Wider invisible hit area so thin tracks are easy to click
          trackGroup
            .append("path")
            .attr("d", d)
            .attr("fill", "none")
            .attr("stroke", "transparent")
            .attr("stroke-width", 10)
            .attr("vector-effect", "non-scaling-stroke");

          // Only for the selected track: head (newest IPP) + satellite name,
          // otherwise labels on every track clutter the map
          if (!isSelected) continue;
          const head = projected[projected.length - 1];
          trackGroup.raise(); // selected track above the others
          const headG = trackGroup
            .append("g")
            .attr("class", "zoom-inv")
            .attr("data-x", head[0])
            .attr("data-y", head[1]);
          headG
            .append("circle")
            .attr("r", 4)
            .attr("fill", color)
            .attr("stroke", "#fff")
            .attr("stroke-width", 1.2);
          headG
            .append("text")
            .attr("x", 6)
            .attr("y", -5)
            .attr("font-size", "12px")
            .attr("font-weight", "700")
            .attr("fill", color)
            .attr("stroke", "#fff")
            .attr("stroke-width", 3)
            .attr("paint-order", "stroke")
            .text(isLive ? satLabel : satLabel + " (no live data)");
        }
      }
    }

    // --- Station marker (drawn LAST, always on top) ---
    if (stationInfo?.latLng) {
      const coord: [number, number] = [stationInfo.latLng[1], stationInfo.latLng[0]];
      const pt = this.projection(coord);
      if (pt) {
        const openPanel = () => this.props.handleOpenMapPanel(null);

        const stationG = this.stationGroup
          .append("g")
          .attr("class", "zoom-inv")
          .attr("data-x", pt[0])
          .attr("data-y", pt[1]);

        // Pulse ring
        stationG
          .append("circle")
          .attr("cx", 0)
          .attr("cy", 0)
          .attr("r", 8)
          .attr("fill", "none")
          .attr("stroke", COLORS.stationPulse)
          .attr("stroke-width", 1.5)
          .attr("opacity", 0.5)
          .attr("class", "station-pulse");

        // Clickable hit area (larger invisible circle for easier clicking)
        stationG
          .append("circle")
          .attr("cx", 0)
          .attr("cy", 0)
          .attr("r", 12)
          .attr("fill", "transparent")
          .style("cursor", "pointer")
          .on("click", openPanel);

        // Station dot
        stationG
          .append("circle")
          .attr("cx", 0)
          .attr("cy", 0)
          .attr("r", 4)
          .attr("fill", COLORS.stationFill)
          .attr("stroke", COLORS.stationStroke)
          .attr("stroke-width", 1.5)
          .style("cursor", "pointer")
          .on("mousemove", (event: MouseEvent) =>
            this.showTooltip(event, stationId.toUpperCase())
          )
          .on("mouseout", () => this.hideTooltip())
          .on("click", openPanel);

        // Label (clickable)
        stationG
          .append("text")
          .attr("x", 7)
          .attr("y", 4)
          .attr("font-size", "11px")
          .attr("font-weight", "600")
          .attr("fill", COLORS.labelFill)
          .style("cursor", "pointer")
          .on("click", openPanel)
          .on("mousemove", (event: MouseEvent) =>
            this.showTooltip(event, "Open satellite panel")
          )
          .on("mouseout", () => this.hideTooltip())
          .text(stationId.toUpperCase());
      }
    }

    this.applyZoomInvariant();
  }

  /** Markers and labels keep their on-screen size: counter-scale them by 1/zoom */
  private applyZoomInvariant() {
    const k = this.zoomK;
    d3.select(this.svgRef)
      .selectAll<SVGGElement, unknown>(".zoom-inv")
      .attr("transform", function () {
        return `translate(${this.getAttribute("data-x")},${this.getAttribute("data-y")}) scale(${1 / k})`;
      });
  }

  zoomBy = (factor: number) => {
    (d3.select(this.svgRef) as any).transition().duration(300).call(this.zoomBehavior.scaleBy, factor);
  };

  resetZoom = () => {
    const svg = d3.select(this.svgRef) as any;
    svg
      .transition()
      .duration(500)
      .call(this.zoomBehavior.transform, d3.zoomIdentity);
    svg.select(".map-root").transition().duration(500).attr("transform", null);
  };

  componentDidMount() {
    this.initMap();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.constellation !== this.props.constellation) {
      this.initMap();
      return;
    }
    if (
      prevProps.ionoData !== this.props.ionoData ||
      prevProps.selectedSatellite !== this.props.selectedSatellite
    ) {
      this.drawContent();
    }
    if (prevProps.flashKey !== this.props.flashKey) {
      this.flashSelectedTrack();
    }
  }

  /** Short blink of the selected track, to show which track the time series belongs to */
  private flashSelectedTrack() {
    if (!this.contentGroup) return;
    const line = this.contentGroup.select(".sat-track.selected .track-line");
    if (line.empty()) return;
    line
      .interrupt()
      .transition().duration(250).attr("stroke-width", 8)
      .transition().duration(250).attr("stroke-width", 3.5)
      .transition().duration(250).attr("stroke-width", 8)
      .transition().duration(250).attr("stroke-width", 3.5);
  }

  render() {
    return (
      <div className="relative">
        {this.state.tooltipVisible && (
          <div
            className="absolute z-50 rounded bg-gray-800 px-2 py-1 text-xs text-white shadow pointer-events-none whitespace-nowrap"
            style={{
              left: this.state.tooltipLeft,
              top: this.state.tooltipTop,
            }}
          >
            {this.state.tooltipContent}
          </div>
        )}
        <div className="relative w-full overflow-hidden">
          <svg
            className="w-full h-auto"
            ref={(ref: SVGSVGElement) => (this.svgRef = ref)}
            viewBox="0 0 960 500"
            preserveAspectRatio="xMidYMid meet"
            style={{ background: COLORS.ocean }}
          />
          {/* Zoom controls */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
            <button
              onClick={() => this.zoomBy(1.6)}
              aria-label="Zoom in"
              className="h-7 w-7 rounded-md bg-brand-300 text-base font-bold leading-none text-white shadow hover:bg-brand-400 transition"
            >
              +
            </button>
            <button
              onClick={() => this.zoomBy(1 / 1.6)}
              aria-label="Zoom out"
              className="h-7 w-7 rounded-md bg-brand-300 text-base font-bold leading-none text-white shadow hover:bg-brand-400 transition"
            >
              −
            </button>
            <button
              onClick={this.resetZoom}
              className="h-7 rounded-md bg-brand-300 px-3 text-xs font-semibold uppercase text-white shadow hover:bg-brand-400 transition"
            >
              Reset Zoom
            </button>
          </div>

          {/* Legend */}
          <div className="absolute top-2 left-2 max-w-[16rem] rounded-md bg-white/90 px-2.5 py-1.5 text-[11px] leading-snug text-gray-600 shadow-sm pointer-events-none">
            <div className="flex items-center gap-2">
              <svg width="22" height="6"><line x1="1" y1="3" x2="21" y2="3" stroke="#4e79a7" strokeWidth="2.5" strokeLinecap="round" /></svg>
              Live track
            </div>
            <div className="flex items-center gap-2">
              <svg width="22" height="6"><line x1="1" y1="3" x2="21" y2="3" stroke="#4e79a7" strokeWidth="2" strokeDasharray="5 3" opacity="0.6" /></svg>
              No live data (hover for the reason)
            </div>
            <p className="mt-1 text-gray-500">
              Each track is the latest pass of the satellite (up to 30 min). Tracks near the horizon look
              longer: the IPP is farther from the station and moves faster.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default MapD3;
